import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { LANGUAGES, TRANSLATIONS } from "./translations";

const I18nContext = createContext(null);

const STORAGE_KEY = "sw_lang";
const DEFAULT_LANG = "en";

function detectInitialLang() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && TRANSLATIONS[saved]) return saved;
  } catch (err) {
    console.warn("i18n: localStorage unavailable", err);
  }
  // Auto-detect from browser
  const nav = (typeof navigator !== "undefined" && (navigator.language || "")).toLowerCase();
  if (nav.startsWith("mr")) return "mr";
  if (nav.startsWith("hi")) return "hi";
  return DEFAULT_LANG;
}

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(detectInitialLang);

  // Apply font + lang attribute to <html> for Devanagari rendering
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang;
      document.documentElement.dataset.lang = lang;
    }
  }, [lang]);

  const setLang = useCallback((code) => {
    if (!TRANSLATIONS[code]) return;
    setLangState(code);
    try {
      localStorage.setItem(STORAGE_KEY, code);
    } catch (err) {
      console.warn("i18n: failed to persist lang", err);
    }
  }, []);

  const t = useCallback((key, params) => {
    const dict = TRANSLATIONS[lang] || TRANSLATIONS[DEFAULT_LANG];
    let str = dict[key];
    if (str == null) {
      // Fallback to English then to key itself
      str = TRANSLATIONS[DEFAULT_LANG][key] ?? key;
    }
    if (params && typeof str === "string") {
      Object.keys(params).forEach((p) => {
        str = str.replaceAll(`{${p}}`, params[p]);
      });
    }
    return str;
  }, [lang]);

  const value = useMemo(
    () => ({ lang, setLang, t, languages: LANGUAGES }),
    [lang, setLang, t]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}
